import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent, MAX_PLATE_LENGTH, sanitizePlateInput } from 'shared-lib';
import { QuickEasypassService } from '../../../api/gen';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';
import { QuickEasypassResultDto } from '../../../api/gen/model/quickEasypassResultDto';

export interface QuickEasypassDialogData {
    parkingSpace: ParkingSpaceDto;
}

/**
 * The cashier's desk form: a plate and nothing else. The cut-off, the payer and the space all come from
 * the space's own Quick Easypass settings, so there is nothing else to ask.
 *
 * The server returns the existing pass when the same plate is submitted twice for the same cut-off, so a
 * double click cannot mint two passes (or two organization bills).
 */
@Component({
    selector: 'app-quick-easypass-dialog',
    standalone: true,
    imports: [FormsModule, MatDialogModule, MatIconModule, JjamesButtonComponent, TranslatePipe],
    templateUrl: './quick-easypass-dialog.component.html'
})
export class QuickEasypassDialogComponent {
    private dialogRef = inject(MatDialogRef<QuickEasypassDialogComponent>);
    private quickEasypassService = inject(QuickEasypassService);
    data = inject<QuickEasypassDialogData>(MAT_DIALOG_DATA);

    licensePlate = signal('');
    isSaving = signal(false);
    errorMessage = signal<string | null>(null);
    result = signal<QuickEasypassResultDto | null>(null);

    readonly maxPlateLength = MAX_PLATE_LENGTH;

    canIssue = computed(() => !this.isSaving() && this.licensePlate().length >= 2);

    /**
     * Drops spaces, hyphens and anything else a plate can never contain as it is typed or pasted, so the
     * cashier sees the same separator-free form the gate cameras read. The caret is restored by hand so a
     * rejected character typed mid-plate does not throw it to the end of the field.
     */
    onLicensePlateInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const raw = input.value;
        const cleaned = sanitizePlateInput(raw);
        if (cleaned === raw) return;

        const caret = input.selectionStart ?? raw.length;
        const rejectedBeforeCaret = caret - sanitizePlateInput(raw.slice(0, caret)).length;
        this.licensePlate.set(cleaned);
        input.value = cleaned;
        const restored = Math.max(0, caret - rejectedBeforeCaret);
        input.setSelectionRange(restored, restored);
    }

    /**
     * "bis 23:59": the pass ends at the cut-off instant itself (00:00 local), so the last valid minute is
     * the one before it, read in the space's own zone and never the browser's.
     */
    validUntil = computed(() => {
        const validTo = this.result()?.validTo;
        if (!validTo) return '';
        const lastMinute = new Date(new Date(validTo).getTime() - 60_000);
        return new Intl.DateTimeFormat('de-AT', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: this.data.parkingSpace.quickEasypassTimeZoneId ?? 'Europe/Vienna'
        }).format(lastMinute);
    });

    onIssue(): void {
        if (!this.canIssue()) return;

        this.isSaving.set(true);
        this.errorMessage.set(null);

        this.quickEasypassService
            .quickEasypassParkingSpaceIdPost(this.data.parkingSpace.id!, { licensePlate: this.licensePlate() })
            .subscribe({
                next: (result) => {
                    this.isSaving.set(false);
                    this.result.set(result);
                },
                error: (err) => {
                    this.isSaving.set(false);
                    console.error('Failed to issue Quick Easypass', err);
                    this.errorMessage.set(err?.error?.detail ?? err?.error?.title ?? 'Could not issue the pass.');
                }
            });
    }

    onAnother(): void {
        this.result.set(null);
        this.licensePlate.set('');
        this.errorMessage.set(null);
    }

    onClose(): void {
        this.dialogRef.close(this.result() !== null);
    }
}
